import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "../lib/api";
import { Clock, ArrowRight } from "lucide-react";
import LeadForm from "../components/site/LeadForm";
import AuthorBioCard from "../components/site/AuthorBioCard";
import Seo, { articleLd } from "../lib/seo";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="container-wide py-16">
      <Seo
        title="Physio Hyve Journal — Physiotherapy & Rehab Articles"
        description="Evidence-informed articles on rehabilitation, sports injuries, neuro recovery and everyday movement from Physio Hyve Clinic."
        path="/blog"
      />
      <div className="max-w-3xl">
        <div className="eyebrow mb-3">Physio Hyve journal</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">Clinical insight, written for patients.</h1>
        <p className="text-brand-ink2 mt-5">Evidence-informed articles on rehabilitation, sports injuries, neuro recovery and everyday movement.</p>
      </div>

      {loading ? (
        <div className="mt-10 text-brand-ink2">Loading articles…</div>
      ) : (
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="card-soft overflow-hidden group" data-testid={`blog-${p.slug}`}>
              {p.featured_image && (
                <div className="aspect-[16/10] overflow-hidden bg-brand-cream">
                  <img src={p.featured_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-3 text-xs text-brand-ink2">
                  <span className="uppercase tracking-widest text-brand-terracotta">{p.category}</span>
                  <span className="flex items-center gap-1"><Clock size={12}/> {p.reading_time || 3} min</span>
                </div>
                <h3 className="font-heading text-xl text-brand-ink mt-3 group-hover:text-brand-olive">{p.title}</h3>
                <p className="text-sm text-brand-ink2 mt-2 line-clamp-3">{p.excerpt}</p>
                <div className="mt-4 text-sm text-brand-olive inline-flex items-center gap-1">Read <ArrowRight size={14}/></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch((e) => setErr(e));
  }, [slug]);

  if (err) return <div className="container-wide py-24"><h1 className="font-heading text-3xl">Article not found</h1><Link to="/blog" className="btn-outline mt-6">Back to blog</Link></div>;
  if (!post) return <div className="container-wide py-24 text-brand-ink2">Loading…</div>;

  // Very light markdown → html for headings + paragraphs
  const rawHtml = post.content
    .split(/\n\n+/)
    .map((block) => {
      if (block.startsWith("## ")) return `<h2>${block.slice(3)}</h2>`;
      if (block.startsWith("### ")) return `<h3>${block.slice(4)}</h3>`;
      if (block.startsWith("- ")) {
        const items = block.split("\n").map((l) => `<li>${l.replace(/^-\s*/, "")}</li>`).join("");
        return `<ul>${items}</ul>`;
      }
      return `<p>${block}</p>`;
    })
    .join("");
  // Sanitize before injecting — prevents any XSS if a blog post ever contains untrusted markup.
  const html = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ["h2", "h3", "h4", "p", "ul", "ol", "li", "strong", "em", "a", "br", "blockquote", "code"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });

  // Map author name to their dedicated profile URL for SEO byline + JSON-LD.
  // Extendable: add new (surname → path) entries as more clinicians publish.
  const authorProfilePaths = {
    "amit sharma": "/team/amit-sharma",
    "manishika patait": "/maternal-care",
  };
  const authorLower = (post.author || "").toLowerCase();
  const authorProfilePath = Object.entries(authorProfilePaths).find(([name]) =>
    authorLower.includes(name)
  )?.[1];
  const authorUrl =
    authorProfilePath && typeof window !== "undefined"
      ? `${window.location.origin}${authorProfilePath}`
      : undefined;

  return (
    <div className="container-wide py-16 grid md:grid-cols-3 gap-10">
      <Seo
        title={`${post.title} | Physio Hyve`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.featured_image}
        ogType="article"
        jsonLd={articleLd({
          title: post.title,
          description: post.excerpt,
          image: post.featured_image,
          author: post.author,
          authorUrl,
          datePublished: post.published_at,
          url: typeof window !== "undefined" ? window.location.href : "",
        })}
      />
      <article className="md:col-span-2">
        <div className="text-xs uppercase tracking-widest text-brand-terracotta">{post.category}</div>
        <h1 className="font-heading text-4xl md:text-5xl text-brand-ink mt-2 leading-[1.05]">{post.title}</h1>
        <div className="mt-4 text-sm text-brand-ink2 flex flex-wrap gap-4">
          <span>
            By{" "}
            {authorProfilePath ? (
              <Link
                to={authorProfilePath}
                className="text-brand-olive hover:underline"
                data-testid="blog-author-link"
              >
                {post.author}
              </Link>
            ) : (
              post.author
            )}
          </span>
          <span className="flex items-center gap-1"><Clock size={12}/> {post.reading_time || 3} min read</span>
        </div>
        {post.featured_image && (
          <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/9] bg-brand-cream">
            <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="prose-hyve mt-8" dangerouslySetInnerHTML={{ __html: html }} />
        <AuthorBioCard authorName={post.author} />

      </article>
      <aside className="md:sticky md:top-24 self-start">
        <LeadForm sourcePage={`blog-${slug}`} category={post.category} />
      </aside>
    </div>
  );
}
